import React, { useEffect, useState } from 'react';
import { Clock, Timer, Hand } from 'lucide-react';
import { NumberPadField } from './NumberPadField';
import { realtime } from '../lib/realtime';

export type StartMode = 'none' | 'timer' | 'clock';

export interface StartChoice {
  mode: StartMode;
  /** Minutos del temporizador. */
  minutes: number;
  /** "HH:MM" en la hora local de quien elige. */
  clock: string;
}

export const DEFAULT_START: StartChoice = { mode: 'none', minutes: 5, clock: '' };

/**
 * La hora de inicio elegida, ya en el reloj del servidor, o null si no se
 * fijó ninguna. El servidor es quien la cuenta, así que se traduce con el
 * desfase medido en el canal en vivo: si el reloj de este equipo va adelantado,
 * la partida igualmente empieza cuando dice el servidor para todos.
 */
export function startAtFromChoice(choice: StartChoice): number | null {
  let local: number | null = null;
  if (choice.mode === 'timer') {
    local = Date.now() + choice.minutes * 60_000;
  } else if (choice.mode === 'clock' && /^\d{2}:\d{2}$/.test(choice.clock)) {
    const [h, m] = choice.clock.split(':').map(Number);
    const target = new Date();
    target.setHours(h, m, 0, 0);
    // Una hora que hoy ya pasó es la de mañana: nadie programa en el pasado.
    if (target.getTime() <= Date.now()) target.setDate(target.getDate() + 1);
    local = target.getTime();
  }
  if (local === null) return null;
  return local + (realtime.serverNow() - Date.now());
}

/** La hora local en "HH:MM" dentro de unos minutos, para proponer algo razonable. */
function clockIn(minutes: number): string {
  const d = new Date(Date.now() + minutes * 60_000);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

interface Props {
  value: StartChoice;
  onChange: (choice: StartChoice) => void;
  /** Cómo empieza la partida si no se fija hora (depende de si hay anfitrión). */
  manualLabel: string;
  manualHint: string;
}

/** Cómo empieza la partida: a mano, con un temporizador o a una hora fija. */
export const StartTimePicker: React.FC<Props> = ({ value, onChange, manualLabel, manualHint }) => {
  const set = (patch: Partial<StartChoice>) => onChange({ ...value, ...patch });

  // Al elegir "a una hora" se propone una dentro de un cuarto de hora, en vez
  // de un campo vacío que no dice qué formato espera.
  useEffect(() => {
    if (value.mode === 'clock' && !value.clock) set({ clock: clockIn(15) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.mode]);

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-ink-300 mb-2">Cuándo empieza</legend>
      <div className="grid grid-cols-3 gap-2">
        <ModeButton
          active={value.mode === 'none'}
          onClick={() => set({ mode: 'none' })}
          icon={<Hand className="w-4 h-4" />}
          label={manualLabel}
        />
        <ModeButton
          active={value.mode === 'timer'}
          onClick={() => set({ mode: 'timer' })}
          icon={<Timer className="w-4 h-4" />}
          label="Temporizador"
        />
        <ModeButton
          active={value.mode === 'clock'}
          onClick={() => set({ mode: 'clock' })}
          icon={<Clock className="w-4 h-4" />}
          label="A una hora"
        />
      </div>

      {value.mode === 'none' && <p className="text-xs text-ink-500">{manualHint}</p>}

      {value.mode === 'timer' && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-ink-400">Empieza sola dentro de</span>
          <NumberPadField
            value={value.minutes}
            onChange={(minutes) => set({ minutes })}
            min={1}
            max={180}
            label="Minutos del temporizador"
            unit="min"
          />
        </div>
      )}

      {value.mode === 'clock' && (
        <div className="flex items-center gap-3 flex-wrap">
          <label htmlFor="start-clock" className="text-sm text-ink-400">
            Empieza sola a las
          </label>
          <input
            id="start-clock"
            type="time"
            value={value.clock}
            onChange={(e) => set({ clock: e.target.value })}
            className="h-11 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-lg font-semibold
                       outline-none focus:border-sky-600"
          />
          <ClockHint clock={value.clock} />
        </div>
      )}

      {value.mode !== 'none' && (
        <p className="text-xs text-ink-500">
          Todos en la sala ven la cuenta atrás. Si a esa hora no hay nadie con quien jugar, la sala
          sigue esperando.
        </p>
      )}
    </fieldset>
  );
};

/** "Hoy" o "mañana", para que quede claro qué día cae la hora elegida. */
const ClockHint: React.FC<{ clock: string }> = ({ clock }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);
  if (!/^\d{2}:\d{2}$/.test(clock)) return null;
  const [h, m] = clock.split(':').map(Number);
  const target = new Date();
  target.setHours(h, m, 0, 0);
  const tomorrow = target.getTime() <= Date.now();
  return <span className="text-xs text-ink-500">{tomorrow ? 'mañana' : 'hoy'}</span>;
};

const ModeButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl border-2 text-xs font-semibold transition-colors ${
      active
        ? 'bg-sky-600/15 border-sky-500 text-ink-50'
        : 'bg-ink-850 border-ink-700 text-ink-400 hover:border-ink-600'
    }`}
  >
    <span className={active ? 'text-sky-400' : ''}>{icon}</span>
    {label}
  </button>
);

/**
 * Cuenta atrás hasta una hora del servidor, en "mm:ss" (o "h:mm:ss").
 * Se usa en la sala y en la sala de espera.
 */
export function useCountdown(serverAt: number | null): number | null {
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!serverAt) {
      setLeft(null);
      return;
    }
    const update = () => setLeft(Math.max(0, serverAt - realtime.serverNow()));
    update();
    const id = window.setInterval(update, 250);
    return () => window.clearInterval(id);
  }, [serverAt]);
  return left;
}

export function formatCountdown(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(h > 0 ? 2 : 1, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Hora local de una hora del servidor, en "HH:MM". */
export function localClockOf(serverAt: number): string {
  const local = serverAt - (realtime.serverNow() - Date.now());
  return new Date(local).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
}
